/**
 * Approved product-area colors (Jens 2026-08-17).
 *
 * The pastel tiles from the website's "Marketing that remembers" section —
 * the numbered cards on profitjam.io and the Platform menu's icon boxes.
 * `bg` fills an icon tile; `border` is the matching accent from the same
 * approved list.
 *
 * Shared so every surface tints its icons from the SAME list instead of each
 * screen inventing colors.
 */
export interface AreaTile {
  bg: string
  border: string
}

export interface ChannelIconTile extends AreaTile {
  foreground: string
}

export const AREA_TILES = {
  businessBrain:    { bg: '#E8FAFD', border: '#8EDDEA' }, // 01 Business Brain
  campaignsContent: { bg: '#EAF3FF', border: '#A8CFF7' }, // 02 Campaigns & Content
  publishNurture:   { bg: '#F3EAFF', border: '#CDB0EF' }, // 03 Publish & Nurture
  mediaStudio:      { bg: '#FFF1EA', border: '#F4BEA8' }, // 04 Media Studio
  automations:      { bg: '#FAF9CD', border: '#E3D767' }, // 05 Automations
  trackingResults:  { bg: '#ECECFF', border: '#B8BBF2' }, // 06 Tracking & Results
  websitesLp:       { bg: '#FCEAF3', border: '#E7B0CF' }, // 07 Websites & Landing Pages
  commercePayments: { bg: '#E8F8EF', border: '#9BD8B7' }, // 08 Commerce & Payments
} as const satisfies Record<string, AreaTile>

/**
 * Product features that appear both in the app and inside the page builder.
 * They deliberately reuse their parent product-area palette so the same
 * object never acquires a second, builder-only identity color.
 */
export const FEATURE_TILES = {
  collections: AREA_TILES.websitesLp,
  forms: AREA_TILES.campaignsContent,
  catalog: AREA_TILES.commercePayments,
  discounts: AREA_TILES.commercePayments,
  shipping: AREA_TILES.commercePayments,
  campaigns: AREA_TILES.campaignsContent,
  content: AREA_TILES.campaignsContent,
  sequences: AREA_TILES.automations,
  agents: AREA_TILES.automations,
  savedSections: AREA_TILES.publishNurture,
} as const satisfies Record<string, AreaTile>

/**
 * Content-channel colors from CreateContentModal's channel picker.
 * Keep list rows and the picker on this one palette so channel identity is
 * recognizable before the merchant reads the label.
 */
export const CONTENT_CHANNEL_TILES = {
  email:  { bg: '#DBEAFE', border: '#93C5FD', foreground: '#1E3A8A' },
  social: { bg: '#FCE7F3', border: '#F9A8D4', foreground: '#9D174D' },
  blog:   { bg: '#DCFCE7', border: '#86EFAC', foreground: '#166534' },
} as const satisfies Record<'email' | 'social' | 'blog', ChannelIconTile>
